import React, { useState } from 'react'
import {
  Drawer,
  Box,
  Typography,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
} from '@mui/material'
import { Delete as DeleteIcon } from '@mui/icons-material'
import { format } from 'date-fns'
import {
  PaymentBatch,
  ProviderPayout,
  ProviderInvoiceLine,
  formatCurrency,
} from '../mockData'

interface ProviderBatchDetailDrawerProps {
  open: boolean
  onClose: () => void
  batch: PaymentBatch
  provider: ProviderPayout | null
  onRemoveInvoice?: (invoiceId: string) => Promise<void> | void
}

const ProviderBatchDetailDrawer: React.FC<ProviderBatchDetailDrawerProps> = ({
  open,
  onClose,
  batch,
  provider,
  onRemoveInvoice,
}) => {
  const isClaim = batch.batchType === 'claim'
  const [lineToRemove, setLineToRemove] = useState<ProviderInvoiceLine | null>(
    null
  )
  const [removing, setRemoving] = useState(false)

  const handleConfirmRemove = async () => {
    if (!lineToRemove?._id || !onRemoveInvoice) return
    setRemoving(true)
    try {
      await onRemoveInvoice(lineToRemove._id)
      setLineToRemove(null)
    } finally {
      setRemoving(false)
    }
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ zIndex: theme => theme.zIndex.drawer + 2 }}
      PaperProps={{ sx: { width: { xs: '100%', sm: 440 }, p: 3 } }}
    >
      {provider && (
        <>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {provider.name} — Batch Detail
            </Typography>
          </Box>

          <SectionLabel>BATCH INFORMATION</SectionLabel>
          <InfoRow label="Batch Name" value={batch.name} />
          <InfoRow label="Month" value={batch.month} />
          <InfoRow label="Created By" value={batch.createdBy} />
          <InfoRow
            label="Created Date"
            value={safeDate(batch.createdDate)}
          />
          {batch.status !== 'Ready for Payment' && (
            <InfoRow
              label="Status"
              value={
                <Chip
                  label={batch.status}
                  size="small"
                  sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 500 }}
                />
              }
            />
          )}

          <Divider sx={{ my: 2.5 }} />

          <SectionLabel>PROVIDER BREAKDOWN</SectionLabel>
          <InfoRow label="Provider" value={provider.name} />
          <InfoRow label="Sessions" value={String(provider.sessions)} />
          <InfoRow
            label={isClaim ? 'Claim Count' : 'Invoice Count'}
            value={String(provider.invoiceCount)}
          />
          <InfoRow
            label="Payable Amount"
            value={
              <Typography sx={{ fontWeight: 700, color: '#7A6FA1' }}>
                {formatCurrency(provider.payableAmount)}
              </Typography>
            }
          />

          <Divider sx={{ my: 2.5 }} />

          <SectionLabel>
            {isClaim ? 'CLAIM BREAKDOWN' : 'INVOICE BREAKDOWN'}
          </SectionLabel>
          {provider.invoices.map(line => (
            <Box
              key={line._id || line.invoice_ids}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                py: 0.75,
                gap: 1,
              }}
            >
              <Typography variant="body2" sx={{ color: '#7A6FA1' }}>
                {line.invoice_ids}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {line.amount > 0 ? formatCurrency(line.amount) : '—'}
                </Typography>
                {onRemoveInvoice && line._id && (
                  <Tooltip
                    placement="top"
                    title={isClaim ? 'Remove claim' : 'Remove invoice'}
                  >
                    <IconButton
                      size="small"
                      onClick={() => setLineToRemove(line)}
                      sx={{ color: '#dc2626' }}
                    >
                      <DeleteIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
          ))}

          <Divider sx={{ my: 2.5 }} />

          <SectionLabel>PAYMENT TIMELINE</SectionLabel>
          {batch.timeline.map(entry => (
            <Box
              key={entry.label}
              sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor: '#7A6FA1',
                  mt: 0.5,
                }}
              />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {entry.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {safeDateTime(entry.timestamp)}
                </Typography>
              </Box>
            </Box>
          ))}
        </>
      )}

      <Dialog
        open={Boolean(lineToRemove)}
        onClose={() => (removing ? null : setLineToRemove(null))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Remove {isClaim ? 'claim' : 'invoice'} from batch
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove{' '}
            <strong>{lineToRemove?.invoice_ids}</strong> from this batch? This
            action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1 }}>
          <Button
            onClick={() => setLineToRemove(null)}
            disabled={removing}
            variant="outlined"
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmRemove}
            disabled={removing}
            variant="contained"
            color="error"
            startIcon={
              removing ? (
                <CircularProgress size={16} sx={{ color: 'white' }} />
              ) : (
                <DeleteIcon />
              )
            }
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            {removing ? 'Removing…' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  )
}

const safeDate = (value: string): string => {
  try {
    return format(new Date(value), 'MM/dd/yyyy')
  } catch {
    return value
  }
}

const safeDateTime = (value: string): string => {
  try {
    return format(new Date(value), 'MM/dd/yyyy • hh:mm a')
  } catch {
    return value
  }
}

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant="caption"
    sx={{
      display: 'block',
      fontWeight: 700,
      letterSpacing: '0.06em',
      color: '#64748b',
      mb: 1.5,
    }}
  >
    {children}
  </Typography>
)

const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      py: 0.75,
    }}
  >
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    {React.isValidElement(value) ? (
      value
    ) : (
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {value == null || typeof value === 'object' ? '—' : value}
      </Typography>
    )}
  </Box>
)

export default ProviderBatchDetailDrawer
